import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import {
  emptyProfileReactionCounts,
  totalProfileReactions,
  type ProfileReactionCounts,
} from '@/src/features/profile/profile-service';

export type InviteProfileStats = {
  followerCount: number;
  followingCount: number;
  inviteCount: number;
  reactionCounts: ProfileReactionCounts;
  totalLikeCount: number;
};

export type ResolvedInviteProfile = {
  uid: string;
  fullName: string | null;
  username: string | null;
  photoURL: string | null;
  stats: InviteProfileStats;
};

export type ResolveInviteCodeResult = {
  inviter: ResolvedInviteProfile | null;
  valid: boolean;
};

type GetMyInviteLinkResult = {
  code: string;
  url: string;
};

const getMyInviteLinkCallable = httpsCallable<
  Record<string, never>,
  GetMyInviteLinkResult
>(functions, 'getMyInviteLink');
const resolveInviteCodeCallable = httpsCallable<
  { code: string },
  ResolveInviteCodeResult
>(functions, 'resolveInviteCode');

function numberOrZero(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : 0;
}

function parseReactionCounts(value: unknown): ProfileReactionCounts {
  return {
    ...emptyProfileReactionCounts(),
    ...(value && typeof value === 'object' ? value : {}),
  };
}

function parseInviteStats(value: unknown): InviteProfileStats {
  const data =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  const reactionCounts = parseReactionCounts(data.reactionCounts);

  return {
    followerCount: numberOrZero(data.followerCount),
    followingCount: numberOrZero(data.followingCount),
    inviteCount: numberOrZero(data.inviteCount),
    reactionCounts,
    totalLikeCount:
      typeof data.totalLikeCount === 'number'
        ? numberOrZero(data.totalLikeCount)
        : totalProfileReactions({ reactionCounts }),
  };
}

function parseResolvedInviteProfile(
  value: unknown,
): ResolvedInviteProfile | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (typeof data.uid !== 'string' || !data.uid) {
    return null;
  }

  return {
    uid: data.uid,
    fullName:
      typeof data.fullName === 'string' && data.fullName.trim()
        ? data.fullName.trim()
        : null,
    username:
      typeof data.username === 'string' && data.username.trim()
        ? data.username.trim()
        : null,
    photoURL:
      typeof data.photoURL === 'string' && data.photoURL.trim()
        ? data.photoURL
        : null,
    stats: parseInviteStats(data.stats),
  };
}

export async function getMyInviteLink() {
  const result = await getMyInviteLinkCallable({});

  return result.data;
}

export async function resolveInviteCode(
  code: string,
): Promise<ResolveInviteCodeResult> {
  const result = await resolveInviteCodeCallable({ code });
  const inviter = parseResolvedInviteProfile(result.data.inviter);

  return {
    inviter,
    valid: result.data.valid === true && Boolean(inviter),
  };
}

export async function dismissInviteEducation(uid: string) {
  await updateDoc(doc(db, 'users', uid), {
    inviteEducationDismissedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
